import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

    let added = false;

    try {
      // Remember how many of this variant are already in the cart, so a failed
      // response can be checked against the real cart before showing an error.
      // Read from the already-rendered cart drawer, so no extra request delays the add.
      const quantityBefore = this.#renderedCartVariantQuantity(line.merchandiseId);

      let result;
      try {
        result = await this.#addLine(line, signal);
      } catch (error) {
        if (signal.aborted) throw error;
        console.warn('[s-product-form] Add to cart failed, verifying cart:', error, error?.cause);

        // A temporary failure (network, timeout, busy cart) can happen after the
        // item was already saved. Only retry when the cart shows it was not added.
        // Without a known starting quantity a retry could add the item twice.
        if (quantityBefore == null) throw error;

        if (await this.#wasAdded(line, quantityBefore)) {
          result = {};
          await this.#refreshCarts(signal);
        } else {
          await this.#wait(700, signal);
          result = await this.#addLine(line, signal);
        }
      }

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#showError(errorMessage);
      } else {
        added = true;
      }
    } catch (error) {
      if (signal.aborted) return;

      console.error('[s-product-form] Add to cart failed:', error, error?.cause);

      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }

    // The item is in the cart at this point; a drawer that fails to open must not
    // be reported as a failed cart update.
    if (added && !signal.aborted) {
      try {
        await window.Shopify.actions.openCart?.();
      } catch (error) {
        console.warn('[s-product-form] Cart drawer could not open:', error);
      }
    }
  }

  #addLine(line, signal) {
    return window.Shopify.actions.updateCart(
      { lines: [line] },
      {
        event: {
          context: 'product',
          detail: this.#eventDetail(),
        },
        signal,
      }
    );
  }

  #renderedCartVariantQuantity(variantId) {
    const cart = document.getElementById('cart-drawer-items');
    if (!cart) return null;
    const id = String(variantId).split('/').pop();
    const quantities = new Map();
    cart.querySelectorAll('input[data-quantity-display][data-variant-id]').forEach((input) => {
      if (input.dataset.variantId !== id) return;
      const value = Number.parseInt(input.defaultValue || input.value, 10);
      quantities.set(input.dataset.lineKey || input, Number.isFinite(value) ? value : 0);
    });
    let total = 0;
    quantities.forEach((value) => { total += value; });
    return total;
  }

  async #cartVariantQuantity(variantId) {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return null;
      const cart = await response.json();
      const id = String(variantId).split('/').pop();
      return (cart.items || [])
        .filter((item) => String(item.variant_id) === id)
        .reduce((sum, item) => sum + (item.quantity || 0), 0);
    } catch {
      return null;
    }
  }

  async #wasAdded(line, quantityBefore) {
    if (quantityBefore == null) return false;
    const quantityAfter = await this.#cartVariantQuantity(line.merchandiseId);
    return quantityAfter != null && quantityAfter >= quantityBefore + line.quantity;
  }

  async #refreshCarts(signal) {
    try {
      await customElements.get('s-cart')?.refreshMountedCarts?.({ signal });
    } catch (error) {
      console.warn('[s-product-form] Cart refresh failed:', error);
    }
  }

  #wait(ms, signal) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(resolve, ms);
      signal?.addEventListener('abort', () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      }, { once: true });
    });
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
